// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaSearchPlus, FaGlobe, FaHistory, FaFacebookF, FaTwitter, FaInstagram, FaTimes } from 'react-icons/fa';

// --- SUB-COMPONENTS ---

const CountUp = ({ end, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  useEffect(() => {
    if (inView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) { setCount(end); clearInterval(timer); }
        else { setCount(Math.floor(start)); }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [inView, end, duration]);
  return <span ref={ref}>{count.toLocaleString()}</span>;
};

const EventCard = ({ tag, title, description, img, btnText }) => {

  return (
    <div className="bg-white border border-gray-100 group flex flex-col h-full shadow-sm hover:shadow-lg transition-all duration-300">
      <div className="h-64 overflow-hidden relative">
        <img src={img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt={title} />
        <div className="absolute top-4 left-4 bg-[#f9b50b] text-[#221f1f] text-[10px] font-black px-3 py-1 uppercase tracking-tighter">{tag}</div>
      </div>
      <div className="p-8 flex flex-col flex-grow">
        <h3 className="text-xl font-serif font-bold uppercase mb-4 group-hover:text-[#f9b50b] transition-colors h-14 overflow-hidden">{title}</h3>
        <p className="text-gray-500 text-sm mb-6 line-clamp-3">{description}</p>

        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-4"></div>
        <Link to="/eventsdetail" onClick={() => window.scrollTo(0, 0)} className="block w-full border-2 border-[#f9b50b] text-center py-4 text-xs font-black uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-all">
          {btnText}
        </Link>
      </div>
    </div>
  );
};

// --- DATA ---

const slides = [
  {
    top: "HELP · CONNECT · UNITE",
    heading: "Unity In Diversity",
    sub: "Bringing people of all faiths, cultures, and backgrounds together to build understanding, peace, and shared humanity.",
    btn: "READ MORE",
    link: "/about",
    img: "/Garba(3).jpeg"
  },
  {
    top: "COMMUNITY · FAITH · TOGETHERNESS",
    heading: "Building Bridges Between Hearts",
    sub: "Through interfaith dialogue, community gatherings, and shared celebrations, we create spaces where respect and harmony grow.",
    btn: "OUR EVENTS",
    link: "/events",
    img: "/Community Bash.jpeg"
  },
  {
    top: "UNITY · HUMANITY · PEACE",
    heading: "One Human Family",
    sub: "Inspired by the shared origin of humanity, Adam & Eve Day reminds us that beyond all differences, we stand as one.",
    btn: "JOIN THE MOVEMENT",
    link: "/join",
    img: "/Adam & Eve Day _22.jpg"
  }
];

const testimonials = [
  {
    id: 1,
    quote: "We believe that every child deserves a chance to smile and grow in a safe environment. Your support makes this possible.",
    name: "Asfia Ghost",
    role: "CEO & Founder",
    img: "/1.jpg"
  },
  {
    id: 2,
    quote: "Our mission is to provide education and food to underprivileged children worldwide. Join us in making a real difference.",
    name: "John Doe",
    role: "Director",
    img: "/2.jpg"
  },
  {
    id: 3,
    quote: "Founded with a vision to serve humanity, we have been working since 1990 to provide aid to those who need it most.",
    name: "Sara Wilson",
    role: "Manager",
    img: "/3.jpg"
  }
];

// --- MAIN HOME PAGE ---

const Home = () => {

  // --- ADD THIS COUNTDOWN LOGIC HERE ---
  const targetDate = new Date('February 21, 2026 16:00:00').getTime();
  const [timeLeft, setTimeLeft] = useState({
    days: 0, hours: 0, minutes: 0, seconds: 0
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);


  const [filter, setFilter] = useState('all');
  const [selectedImg, setSelectedImg] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showPopup, setShowPopup] = useState(false); // New State for Popup

  //const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // NEW: Trigger Popup 3 seconds after load
  useEffect(() => {
    const timer = setTimeout(() => setShowPopup(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const emergencyControls = useAnimation();
  const { ref: emRef, inView: emInView } = useInView({ triggerOnce: true, threshold: 0.2 });
  useEffect(() => {
    if (emInView) {
      emergencyControls.start({ width: "91%", transition: { duration: 2, ease: "circOut" } });
    }
  }, [emergencyControls, emInView]);

  const galleryItems = [
    // Adam & Eve Day
    { id: 1, category: 'adam & eve day', img: '/Adam & Eve Day _22.jpg' },
    { id: 2, category: 'adam & eve day', img: '/Adam & Eve Day.jpeg' },
    { id: 3, category: 'adam & eve day', img: '/Adam & Eve Day(1).jpeg' },
    { id: 4, category: 'adam & eve day', img: '/Adam & Eve Day(2).jpeg' },
    { id: 5, category: 'adam & eve day', img: '/Adam & Eve Day(3).jpeg' },
    { id: 6, category: 'adam & eve day', img: '/Adam & Eve Day(4).jpeg' },

    // Community Bash
    { id: 7, category: 'community bash', img: '/Community Bash.jpeg' },
    { id: 8, category: 'community bash', img: '/Community Bash (2).jpeg' },
    { id: 9, category: 'community bash', img: '/Community Bash (3).jpeg' },
    { id: 10, category: 'community bash', img: '/Community Bash (4).jpeg' },

    // Eid Milaad Un Nabi
    { id: 11, category: 'eid milad un nabi', img: '/Eid Milaad Un Nabi.jpeg' },
    { id: 12, category: 'eid milad un nabi', img: '/Eid Milaad Un Nabi (2).jpeg' },
    { id: 13, category: 'eid milad un nabi', img: '/Eid Milaad Un Nabi (3).jpeg' },
    { id: 14, category: 'eid milad un nabi', img: '/Eid Milaad Un Nabi (4).jpeg' },

    // Eid Namaz
    { id: 15, category: 'eid namaz', img: '/Eid Namaz.jpeg' },
    { id: 16, category: 'eid namaz', img: '/Eid Namaz(1).jpeg' },
    { id: 17, category: 'eid namaz', img: '/Eid Namaz(2).jpeg' },
    { id: 18, category: 'eid namaz', img: '/Eid Namaz(3).jpeg' },
    { id: 19, category: 'eid namaz', img: '/Eid Namaz(4).jpeg' },
    { id: 20, category: 'eid namaz', img: '/Eid Namaz(5).jpeg' },
    { id: 21, category: 'eid namaz', img: '/Eid Namaz(6).jpeg' },
    { id: 22, category: 'eid namaz', img: '/Eid Namaz(7).jpeg' },
    { id: 23, category: 'eid namaz', img: '/Eid Namaz(8).jpeg' },
    { id: 24, category: 'eid namaz', img: '/Eid Namaz(9).jpeg' },
    { id: 25, category: 'eid namaz', img: '/Eid Namaz(10).jpeg' },
    { id: 26, category: 'eid namaz', img: '/Eid Namaz(11).jpeg' },
    { id: 27, category: 'eid namaz', img: '/Eid Namaz(12).jpeg' },
    { id: 28, category: 'eid namaz', img: '/Eid Namaz(13).jpeg' },

    // Iftaar Dinner
    { id: 29, category: 'iftar dinner', img: '/Iftaar Dinner.jpeg' },
    { id: 30, category: 'iftar dinner', img: '/Iftaar Dinner(1).jpeg' },
    { id: 31, category: 'iftar dinner', img: '/Iftaar Dinner(2).jpeg' },
    { id: 32, category: 'iftar dinner', img: '/Iftaar Dinner(3).jpeg' },

    // Garba event
    { id: 33, category: 'Garba', img: '/Garba.jpeg' },
    { id: 34, category: 'Garba', img: '/Garba(1).jpeg' },
  ];

  const staff = [
    { name: "John Doe", role: "CEO & Founder", img: "/staff1.jpg" },
    { name: "Jane Smith", role: "Volunteer", img: "/staff1.jpg" },
    { name: "Mike Tyson", role: "Manager", img: "/staff1.jpg" },
    { name: "Sara Wilson", role: "Director", img: "/staff1.jpg" },
  ];

  const filteredItems = filter === 'all' ? galleryItems : galleryItems.filter(i => i.category === filter);

  return (
    <div className="flex flex-col min-h-screen text-[#221f1f] bg-white font-sans overflow-x-hidden">

      {/* 1. HERO SLIDER */}
      <section className="relative min-h-screen md:h-[110vh] flex items-center justify-center text-white overflow-hidden bg-bold">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSlide}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute inset-0 z-0 bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.75), rgba(0,0,0,0.75)), url('${slides[activeSlide].img}')` }} />
        </AnimatePresence>

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.8 }}
            >
              <p className="uppercase tracking-[0.4em] text-[13px] font-bold mb-6 text-[#f9b50b]">
                {slides[activeSlide].top}
              </p>

              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif font-bold mb-6 tracking-normal min-h-[1.2em] flex items-center justify-center capitalize text-center">
                {slides[activeSlide].heading}
              </h1>

              <p className="text-base sm:text-lg md:text-xl italic mb-10 opacity-90 max-w-3xl mx-auto leading-relaxed px-2">
                {slides[activeSlide].sub}
              </p>

              {/* Update 2: Buttons removed from here */}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-4">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setActiveSlide(i)} className={`h-1 transition-all duration-500 ${activeSlide === i ? 'w-12 bg-[#f9b50b]' : 'w-6 bg-white/30'}`} />
          ))}
        </div>
      </section>

      {/* 2. MISSION & STORY BOXES */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">

          {/* Our Mission Box */}
          <div className="p-12 text-center border-t-8 border-[#f9b50b] shadow-xl bg-white group hover:-translate-y-2 transition-all duration-300">
            <FaGlobe className="text-4xl text-[#f9b50b] mx-auto mb-6 group-hover:rotate-12 transition-transform" />
            <h3 className="text-3xl font-serif font-bold uppercase mb-6 text-[#221f1f]">Our Mission</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              The mission of the Illinois Interfaith Organization is to promote unity and diversity by fostering understanding, dialogue, and collaboration across faiths and cultures. We seek to build inclusive communities that uphold human dignity and protect human rights.
            </p>
          </div>

          {/* Our Story Box */}
          <div className="p-12 text-center border-t-8 border-[#f9b50b] shadow-xl bg-white group hover:-translate-y-2 transition-all duration-300">
            <FaHistory className="text-4xl text-[#f9b50b] mx-auto mb-6 group-hover:-rotate-12 transition-transform" />
            <h3 className="text-3xl font-serif font-bold uppercase mb-6 text-[#221f1f]">Our Story</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              We envision a society where individuals from different faiths, cultures, and backgrounds come together in the spirit of respect and cooperation. The Illinois Interfaith Organization works to inspire collective action through meaningful engagement and community-building.
            </p>
          </div>

        </div>
      </section>

      {/* 3. Upcoming Events Section - Redesigned */}
      <section className="py-12 px-6 max-w-7xl mx-auto font-sans">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-serif font-bold uppercase text-[#221f1f]">Upcoming Events</h2>
          <div className="hidden md:block h-[2px] flex-grow mx-8 bg-gray-100"></div>
          <p className="text-[#f9b50b] font-bold uppercase tracking-widest text-[11px]">Mark Your Calendar</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 shadow-xl overflow-hidden rounded-sm border border-gray-100">

          {/* LEFT SIDE: Content */}
          <div className="lg:col-span-7 p-6 md:p-10 flex flex-col justify-center bg-white">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-[#f9b50b] text-[#221f1f] px-2 py-0.5 text-[10px] font-bold uppercase">
                Next Month
              </span>
              <span className="text-gray-400 text-[11px] font-bold uppercase tracking-widest">Feb 21, 2026 • 4:00 PM</span>
            </div>

            <h3 className="text-3xl font-serif font-bold uppercase mb-3 text-[#221f1f] leading-tight">
              Interfaith <span className="text-[#f9b50b]">Annual Dinner</span>
            </h3>

            <p className="text-gray-500 text-sm mb-6 leading-relaxed max-w-lg">
              Meet your candidates and join the community for an evening of dialogue and unity at 1919 Walnut Avenue.
            </p>

            {/* WORKING COMPACT COUNTDOWN */}
            <div className="grid grid-cols-4 gap-2 max-w-xs mb-8">
              {[
                { label: 'Days', value: timeLeft.days },
                { label: 'Hrs', value: timeLeft.hours },
                { label: 'Min', value: timeLeft.minutes },
                { label: 'Sec', value: timeLeft.seconds }
              ].map((item, idx) => (
                <div key={idx} className="bg-gray-50 py-3 rounded-sm border border-gray-100 text-center">
                  <div className="text-xl font-bold text-[#221f1f] leading-none">
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-[9px] font-bold uppercase text-gray-400 mt-1">{item.label}</div>
                </div>
              ))}
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex items-center gap-4">
              <Link
                to="/annual-dinner"
                className="bg-[#221f1f] text-white px-6 py-3 font-bold uppercase text-[11px] tracking-widest hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all"
              >
                View Details
              </Link>
              <Link
                to="/speakers"
                className="border border-[#221f1f]/50 bg-white text-[#221f1f] px-6 py-3 font-bold uppercase text-[11px] tracking-widest hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all"
              >
                View Speakers
              </Link>
            </div>
          </div>

          {/* RIGHT SIDE: Image */}
          <div className="lg:col-span-5 relative h-full lg:h-full overflow-hidden">
            <img
              src="/dinnerinvitation.jpeg"
              alt="Interfaith Dinner"
              className="w-full h-full object-cover"
            />
            <div className="absolute top-0 right-0 bg-[#221f1f] text-white p-4 text-center">
              <p className="text-[#f9b50b] text-xl font-black leading-none">21</p>
              <p className="text-[10px] font-bold uppercase">Feb</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. UPDATED: OUR EVENTS SECTION (Side Overlay Grid Layout) */}
      <section className="py-24 bg-white px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">

          {/* LEFT SIDE CONTENT */}
          <div className="lg:col-span-6 space-y-10">
            <div>
              <h2 className="text-5xl font-serif font-bold uppercase text-[#221f1f] leading-tight">
                EVENTS FOR A{" "}
                <span className="text-[#f9b50b]"> DIVERSE COMMUNITY</span>
              </h2>

              <div className="h-1 w-20 bg-[#f9b50b] mt-4"></div>

              <p className="text-gray-600 text-lg mt-6 leading-relaxed">
                The Illinois Interfaith Organization hosts inclusive gatherings that bring together souls from diverse faiths, cultures, and backgrounds.
                These sacred spaces nurture mutual understanding, trust, and compassion. Each gathering is intentionally designed to invite deep connection and shared spiritual learning.
                Participants meet one another with openness and respect, honoring both differences and common values. Together, they cultivate lasting relationships rooted in unity, reflection, and collective purpose.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Sacred Gatherings", desc: "Nurturing mutual understanding and compassion." },
                { title: "Spiritual Learning", desc: "Shared experiences designed for deep connection." },
                { title: "Unity & Reflection", desc: "Honoring differences while finding common values." },
                { title: "Collective Purpose", desc: "Cultivating lasting relationships built on trust." }
              ].map((item, i) => (
                <div
                  key={i}
                  className="p-4 border-l-4 border-[#f9b50b] bg-gray-50 hover:bg-white hover:shadow-md transition"
                >
                  <h4 className="font-bold uppercase text-xs">{item.title}</h4>
                  <p className="text-gray-500 text-[11px]">{item.desc}</p>
                </div>
              ))}
            </div>

            <Link
              to="/Events"
              className="inline-block bg-[#221f1f] text-white px-12 py-4 font-black uppercase text-xs tracking-widest hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all shadow-xl"
            >
              View All Events
            </Link>
          </div>

          {/* RIGHT SIDE IMAGE + SIDE OVERLAY */}
          <div className="lg:col-span-6 relative h-[450px] md:h-[600px] lg:h-[650px] mt-10 lg:mt-0">

            {/* MAIN BIG IMAGE */}
            <div className="absolute inset-4 md:inset-8 overflow-hidden shadow-2xl">
              <img
                src="/Garba(3).jpeg"
                className="w-full h-full object-cover"
                alt="Main Event"
              />
            </div>

            {/* SIDE OVERLAY PANEL */}
            <div className="
    absolute 
    /* Mobile: Image ke upar (50%) */
    top-[80%] 
    /* Desktop: Normal position (Back to 2/3) */
    lg:top-2/3 
    left-1/2 
    lg:left-auto 
    lg:right-[-100px] 
    -translate-x-1/2 
    lg:translate-x-0 
    -translate-y-1/2 
    w-[90%] 
    lg:w-full 
    backdrop-blur-md 
    p-3 
    md:p-5 
    shadow-2xl 
    z-10"
            >

              {/* IMAGE GRID */}
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                {[
                  "/Garba.jpeg", "/Iftaar Dinner.jpeg", "/Adam & Eve Day.jpeg",
                  "/Eid Namaz.jpeg", "/Garba(1).jpeg", "/Community Bash (4).jpeg",
                  "/Eid Milaad Un Nabi.jpeg", "/Adam & Eve Day(1).jpeg", "/Community Bash.jpeg",
                  "/Eid Namaz(2).jpeg", "/Garba(3).jpeg", "/Iftaar Dinner(2).jpeg"
                ].map((img, i) => (
                  <div
                    key={i}
                    className="aspect-square overflow-hidden shadow-lg transform transition duration-300 hover:scale-110"
                  >
                    <img
                      src={img}
                      alt={`Event ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MOST EMERGENCY CAUSE */}
      <section className="py-24 px-6 max-w-7xl mx-auto hidden">
        <h2 className="text-4xl font-serif font-bold uppercase mb-12">Most Emergency Cause</h2>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-gray-100 shadow-sm overflow-hidden">
          <div className="lg:col-span-7 p-12 flex flex-col justify-center border-r border-gray-100 bg-white">
            <h3 className="text-2xl font-serif font-bold uppercase mb-4">Our Greatest Successes in Unity</h3>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed italic">
              Building understanding across faiths and cultures by bringing communities together through dialogue, service, and shared values.
            </p>
            <div className="mb-10" ref={emRef}>
              <div className="flex justify-between mb-2">
                <span className="font-bold text-sm uppercase text-[#221f1f]">COMMUNITY ENGAGEMENT</span>
                <span className="text-[#f9b50b] font-black">91%</span>
              </div>
              <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={emergencyControls} className="h-full bg-[#f9b50b]" />
              </div>
            </div>
            <div className="flex gap-4">
              <Link to="/causes" className="bg-[#f9b50b] px-8 py-3 font-bold uppercase text-[11px] hover:bg-black hover:text-white transition-all">View More</Link>
              <Link to="/donate" className="border-2 border-gray-200 px-8 py-3 font-bold uppercase text-[11px] hover:bg-[#f9b50b] transition-all">Donate</Link>
            </div>
          </div>
          <div className="lg:col-span-5 bg-[#221f1f] p-12 flex items-center">
            <div className="grid grid-cols-1 gap-10 w-full">
              <div className="flex items-start gap-6">
                <div className="text-center min-w-[100px]">
                  <p className="text-[#f9b50b] text-[10px] font-bold uppercase mb-1">NEEDED</p>
                  <h4 className="text-3xl text-white font-black tracking-tighter">$<CountUp end={40000} /></h4>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed border-l border-white/10 pl-6">For community programs, interfaith events, and outreach initiatives</p>
              </div>
              <div className="flex items-start gap-6">
                <div className="text-center min-w-[100px]">
                  <p className="text-[#f9b50b] text-[10px] font-bold uppercase mb-1">MANAGED</p>
                  <h4 className="text-3xl text-white font-black tracking-tighter">$<CountUp end={35670} /></h4>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed border-l border-white/10 pl-6">Raised and allocated toward inclusive gatherings and awareness efforts</p>
              </div>
              <div className="flex items-start gap-6">
                <div className="text-center min-w-[100px]">
                  <p className="text-[#f9b50b] text-[10px] font-bold uppercase mb-1">DONORS</p>
                  <h4 className="text-3xl text-white font-black tracking-tighter"><CountUp end={312} /></h4>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed border-l border-white/10 pl-6">Community members and supporters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. UPDATED: OUR EVENTS SECTION (Balanced Split Layout) */}
      <section className="py-24 bg-white px-6 hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* LEFT SIDE: Content & Detailed Boxes (Col-span 7) */}
          <div className="lg:col-span-7 space-y-10">
            <div>
              <h2 className="text-5xl font-serif font-bold uppercase text-[#221f1f]">EVENTS FOR A <span className="text-[#f9b50b]">DIVERSE COMMUNITY</span></h2>
              <div className="h-1 w-20 bg-[#f9b50b] mt-4"></div>
              <p className="text-gray-600 text-lg mt-6 leading-relaxed">
                The Illinois Interfaith Organization hosts inclusive events that bring people of diverse faiths, cultures, and backgrounds together to build understanding, trust, and lasting relationships. Each event is designed to encourage meaningful interaction, shared learning, and collective action.
              </p>
            </div>

            {/* Event Description Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: "Interfaith Prayer Gatherings", desc: "These gatherings provide a peaceful and welcoming space for individuals from different faith traditions to come together in reflection and prayer. They honor the spiritual diversity of our community while promoting unity, compassion, and mutual respect." },
                { title: "Cultural Festivals", desc: "Our cultural festivals celebrate the rich traditions, food, music, and art of diverse communities. These events create opportunities for people to learn about one another’s heritage while strengthening appreciation for cultural diversity." },
                { title: "Community Dinners", desc: "Community dinners bring people together around a shared meal, fostering connection and open conversation in a warm and inclusive environment. These gatherings help build friendships, break down barriers, and encourage dialogue across differences." },
                { title: "Guest Speakers Events", desc: "We host speakers from various faiths, cultures, and professional backgrounds to share their experiences and perspectives. These events promote learning, broaden understanding, and inspire thoughtful conversations on faith, culture, and social issues." },
                { title: "Panel Discussions", desc: "Panel discussions bring together diverse voices to explore important topics such as interfaith cooperation, human rights, and social justice. These forums encourage respectful dialogue, critical thinking, and collaborative problem-solving." },
                { title: "Workshops and Training Sessions", desc: "Our workshops and trainings focus on building skills such as leadership, communication, conflict resolution, and community engagement. Participants gain practical tools while strengthening relationships across cultural and faith boundaries." },
                { title: "Volunteer Service Projects", desc: "Volunteer initiatives unite individuals from different backgrounds to serve the community and address shared needs. By working side by side, participants build trust, strengthen relationships, and create a lasting impact through collective action." }

              ].map((item, index) => (
                <div key={index} className="p-6 border-l-4 border-[#f9b50b] bg-gray-50 hover:bg-white hover:shadow-md transition-all">
                  <h4 className="font-bold text-[#221f1f] uppercase text-sm mb-2">{item.title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <Link
              to="/Events"
              onClick={() => window.scrollTo(0, 0)}
              className="inline-block bg-[#221f1f] text-white px-12 py-4 font-black uppercase text-xs tracking-widest hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all shadow-xl"
            >
              View All Events
            </Link>
          </div>

          {/* RIGHT SIDE: Extended Image Collage (Col-span 5) */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4 h-full">
            <div className="space-y-4">
              <img src="/Garba.jpeg" alt="Event 1" className="rounded-lg h-72 w-full object-cover shadow-md" />
              <img src="/Iftaar Dinner.jpeg" alt="Event 2" className="rounded-lg h-56 w-full object-cover shadow-md" />
              <img src="/Community Bash.jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
              <img src="/Community Bash (4).jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
            </div>
            <div className="pt-12 space-y-4">
              <img src="/Adam & Eve Day.jpeg" alt="Event 4" className="rounded-lg h-56 w-full object-cover shadow-md" />
              <img src="/Eid Namaz.jpeg" alt="Event 5" className="rounded-lg h-72 w-full object-cover shadow-md" />
              <img src="/Garba(1).jpeg" alt="Event 6" className="rounded-lg h-60 w-full object-cover shadow-md" />
              <img src="/Eid Milaad Un Nabi (2).jpeg" alt="Event 3" className="rounded-lg h-64 w-full object-cover shadow-md" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR EVENTS SECTION */}
      <section className="py-24 bg-white px-6 hidden">
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h2 className="text-5xl font-serif font-bold uppercase mb-4">Our Events</h2>
          <p className="text-gray-400 uppercase tracking-widest text-xs font-bold italic">Bringing communities together through faith, culture, and shared humanity</p>
          <div className="h-1 w-20 bg-[#f9b50b] mx-auto mt-6"></div>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <EventCard
            tag="COMMUNITY EVENT"
            title="Interfaith Prayer Gatherings"
            description="These gatherings provide a peaceful and welcoming space for individuals from different faith traditions to come together in reflection and prayer. They honor the spiritual diversity of our community while promoting unity, compassion, and mutual respect."
            btnText="LEARN MORE"
            img="/Eid Namaz.jpeg"
          />
          <EventCard
            tag="INTERFAITH GATHERING"
            title="Community Dinners"
            description="Our cultural festivals celebrate the rich traditions, food, music, and art of diverse communities. These events create opportunities for people to learn about one another’s heritage while strengthening appreciation for cultural diversity."
            btnText="VIEW EVENT"
            img="/Iftaar Dinner(1).jpeg"
          />
          <EventCard
            tag="CULTURAL CELEBRATION"
            title="Guest Speaker Events"
            description="We host speakers from various faiths, cultures, and professional backgrounds to share their experiences and perspectives. These events promote learning, broaden understanding, and inspire thoughtful conversations on faith, culture, and social issues."
            btnText="JOIN US"
            img="/Adam & Eve Day(4).jpeg"
          />
        </div>
        <Link
          to="/events"
          onClick={() => window.scrollTo(0, 0)}
          className="mt-16 mx-auto block w-fit bg-[#f9b50b] text-[#221f1f] px-14 py-4 font-black uppercase text-xs tracking-widest hover:bg-black hover:text-white transition-all text-center"
        >
          View All Events
        </Link>
      </section>

      {/* 5. */}
      <section className="relative py-32 my-20 flex flex-col items-center justify-center text-center text-white bg-fixed bg-cover bg-center" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url('/Community Bash.jpeg')` }}>
        <div className="max-w-4xl px-6">
          <h2 className="text-5xl font-serif font-bold uppercase mb-6">WAYS TO SERVE <span className="text-[#f9b50b]">HUMANITY</span></h2>
          <p className="text-base leading-relaxed mb-6 opacity-90 italic">“No single person can change the entire world. But together, we can make a difference in the lives of many — starting today.”</p>
          <h3 className="text-[#f9b50b] font-bold uppercase mb-12">BE THE CHANGE</h3>
          <Link to="/donate" className="bg-[#f9b50b] text-[#221f1f] px-14 py-4 font-black uppercase text-xs tracking-widest hover:bg-white hover:text-[#221f1f] transition-all">Join Us</Link>
        </div>
      </section>

      {/* 8. STAFF */}
      <section className="py-24 bg-white border-t border-gray-100 hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold uppercase mb-4">Meet Our Staff</h2>
            <div className="h-1 w-20 bg-[#f9b50b] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {staff.map((member, i) => (
              <div key={i} className="group relative overflow-hidden bg-gray-900 aspect-[3/4]">
                <img src={member.img} className="w-full h-full object-cover transition-all duration-700 group-hover:opacity-40" alt={member.name} />
                <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                  <h4 className="text-white font-serif font-bold text-xl uppercase">{member.name}</h4>
                  <p className="text-[#f9b50b] text-xs font-bold uppercase tracking-widest mb-6">{member.role}</p>
                  <div className="flex gap-4 text-white"><FaFacebookF /><FaTwitter /><FaInstagram /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. GALLERY */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-serif font-bold uppercase mb-4 text-[#221f1f]">Our Gallery</h2>
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest italic">Capturing moments of unity, faith, and shared humanity</p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {[
            'all',
            'iftar dinner',
            'eid namaz',
            'adam & eve day',
            'eid milad un nabi',
            'community bash',
            'Garba'
          ].map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-10 py-3 text-[11px] font-black uppercase tracking-widest border transition-all duration-500 
        ${filter === cat ? 'bg-[#f9b50b] border-[#f9b50b] text-[#221f1f]' : 'bg-white border-gray-200 hover:text-[#f9b50b] hover:border-[#f9b50b]'}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Images Grid - Sliced to show only 3 */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {filteredItems.slice(0, 3).map((item) => ( // .slice(0, 3) added here
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="relative group overflow-hidden cursor-pointer h-80 shadow-lg border-4 border-white"
                onClick={() => setSelectedImg(item.img)}
              >
                <img
                  src={item.img}
                  alt="Gallery"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#f9b50b]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-center">
                    <FaSearchPlus className="text-white text-4xl mx-auto transform scale-0 group-hover:scale-100 transition-transform duration-300" />
                    <p className="text-white font-black uppercase tracking-widest text-[10px] mt-2">View Larger</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* VIEW ALL BUTTON */}
        <div className="mt-16 flex justify-center">
          <Link
            to="/Events"
            onClick={() => window.scrollTo(0, 0)}
            className="bg-[#221f1f] text-white px-14 py-4 font-black uppercase text-xs tracking-[0.2em] hover:bg-[#f9b50b] hover:text-[#221f1f] transition-all shadow-xl"
          >
            View Events
          </Link>
        </div>
      </section>

      {/* EVENT POPUP MODAL */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-[#221f1f]/90 backdrop-blur-md"
          >
            <motion.div
              initial={{ scale: 0.8, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.8, y: 50, opacity: 0 }}
              className="bg-white max-w-2xl w-full rounded-2xl overflow-hidden relative shadow-[0_0_50px_rgba(249,181,11,0.3)] border border-[#f9b50b]/20"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowPopup(false)}
                className="absolute top-4 right-4 bg-white/80 hover:bg-[#f9b50b] text-[#221f1f] w-10 h-10 rounded-full flex items-center justify-center z-50 transition-all shadow-lg"
              >
                <FaTimes />
              </button>

              {/* Image Section - Using your uploaded Special Invitation Image */}
              <div className="relative aspect-[16/9] bg-gray-100">
                <img
                  src="/specialinvitation.jpeg"
                  alt="Special Invitation Interfaith Dinner 2026"
                  className="w-full h-full object-contain md:object-cover"
                />
              </div>

              {/* Text and Button Section */}
              <div className="p-8 md:p-12 text-center bg-white">

                <p className="text-gray-60 text-sm md:text-base mb-8 leading-relaxed max-w-lg mx-auto italic font-medium">
                  Join the Illinois Interfaith Organization for an evening of meditation, dinner, and dialogue. Meet your local candidates and help build a stronger community.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/annual-dinner"
                    onClick={() => setShowPopup(false)}
                    className="bg-[#f9b50b] text-white px-10 py-4 text-xs font-black uppercase tracking-[0.2em] hover:bg-[#221f1f] hover:text-white transition-all shadow-xl"
                  >
                    View Event Details
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox / Zoom Modal */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center p-4 md:p-10 cursor-zoom-out"
            onClick={() => setSelectedImg(null)}
          >
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} exit={{ scale: 0.5 }} className="relative max-w-5xl w-full">
              <img src={selectedImg} className="w-full h-auto max-h-[85vh] object-contain border-4 border-white shadow-2xl" alt="Enlarged" />
              <button className="absolute -top-12 right-0 text-white text-4xl hover:text-[#f9b50b]" onClick={() => setSelectedImg(null)}>&times;</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 5. TESTIMONIAL SLIDER */}
      {/* <section className="relative bg-[#221f1f] py-20 px-6 overflow-hidden flex items-center">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `url('/Community Bash (3).jpeg')`, backgroundSize: 'cover' }} />
        <div className="max-w-4xl mx-auto relative z-10 w-full">
          <div className="relative border border-[#f9b50b]/30 p-8 md:p-16 text-center">
            <div className="flex justify-center mb-6"><span className="text-[#f9b50b] text-6xl font-serif">“</span></div>
            <div className="relative h-48 md:h-32 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.6 }}
                  className="absolute w-full"
                >
                  <p className="text-white text-base md:text-lg leading-relaxed mb-6 italic opacity-90">{testimonials[currentTestimonial].quote}</p>
                  <div className="w-12 h-0.5 bg-[#f9b50b] mx-auto mb-4"></div>
                  <h4 className="text-white font-serif font-bold uppercase tracking-widest text-sm">
                    {testimonials[currentTestimonial].name} — <span className="text-[#f9b50b]">{testimonials[currentTestimonial].role}</span>
                  </h4>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          <div className="flex justify-center gap-3 mt-10">
            {testimonials.map((_, index) => (
              <button key={index} onClick={() => setCurrentTestimonial(index)} className={`transition-all duration-300 h-2 ${currentTestimonial === index ? 'w-8 bg-[#f9b50b]' : 'w-2 bg-gray-600'}`} />
            ))}
          </div>
        </div>
      </section> */}

      {/* 4. SUBSCRIBE NOW SECTION */}
      <section className="bg-[#1a1818] py-20 px-6 border-y border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-white text-4xl font-serif font-bold uppercase tracking-wider mb-4">
            Subscribe <span className="text-[#f9b50b]">Now</span>
          </h2>
          <p className="text-gray-400 text-sm md:text-base mb-10 font-light max-w-2xl mx-auto leading-relaxed">
            Receive our weekly newsletter with educational materials, upcoming interfaith events,
            and stories of unity from our community.
          </p>
          <form className="flex flex-col md:flex-row items-center justify-center gap-0 max-w-2xl mx-auto shadow-2xl">
            <input
              type="email"
              placeholder="Your Email Address"
              required
              className="w-full md:w-2/3 py-4 px-6 focus:outline-none bg-white text-[#1a1818] text-sm"
            />
            <button
              type="submit"
              className="w-full md:w-1/3 py-4 px-6 bg-[#f9b50b] border-2 border-[#f9b50b] text-[#1a1818] font-bold uppercase tracking-widest hover:bg-transparent hover:text-[#f9b50b] transition-all duration-300 text-xs"
            >
              Subscribe
            </button>
          </form>
          <p className="text-[10px] text-gray-600 uppercase tracking-[0.2em] mt-6">
            Unity in Diversity • Illinois Interfaith
          </p>
        </div>
      </section>

    </div>
  );
};

export default Home;